"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CatalogItem } from "@/lib/airtable";
import { buildPieceHref } from "@/lib/pieceNavigation";
import { ItemStatusTag } from "@/components/ItemStatusTag";

type Props = { items: CatalogItem[] };
type BackContextProps = {
  backHref?: string;
  backLabel?: string;
};

export type SortOption = "popular" | "price-desc" | "price-asc" | "new-arrivals";

type ExternalSortProps = {
  externalSort?: SortOption;
};

// Use an explicit locale so server (Node) and client (browser) produce
// identical formatted strings, avoiding hydration mismatches.
const formatPrice = (n: number) => new Intl.NumberFormat("en-US").format(n);

const shuffleItems = (list: CatalogItem[]) => {
  const result = [...list];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

function getReleaseDateTimestamp(item: CatalogItem): number {
  if (item.releaseDate) {
    const ts = Date.parse(item.releaseDate);
    if (Number.isFinite(ts)) return ts;
  }
  return 0;
}

const PAGE_SIZE = 24;

export default function CatalogClient({ items, backHref, backLabel, externalSort }: Props & BackContextProps & ExternalSortProps) {
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [internalSort, setInternalSort] = useState<SortOption>("popular");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sortOption = externalSort ?? internalSort;
  const showOwnSortBar = externalSort === undefined;

  // Defer Math.random()-based shuffle until after hydration to avoid
  // server/client text content mismatch.
  const [shuffled, setShuffled] = useState<CatalogItem[] | null>(null);
  const prevItemsRef = useRef(items);

  useEffect(() => {
    // Shuffle on mount, and re-shuffle when the items array identity changes.
    if (prevItemsRef.current !== items) {
      prevItemsRef.current = items;
      setVisibleCount(PAGE_SIZE);
    }
    setShuffled(shuffleItems(items));
  }, [items]);

  const handleImageLoaded = (id: string) => {
    setLoadedImages((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  const sortedItems = useMemo(() => {
    if (sortOption === "popular") {
      // Use the shuffled list if available (client), otherwise use original
      // order (server / before hydration) to avoid mismatch.
      return shuffled ?? items;
    }

    const list = [...items];
    const getPrice = (item: CatalogItem) => (typeof item.tagPrice === "number" ? item.tagPrice : null);

    if (sortOption === "new-arrivals") {
      return list.sort((a, b) => {
        const tsA = getReleaseDateTimestamp(a);
        const tsB = getReleaseDateTimestamp(b);
        return tsB - tsA;
      });
    }

    if (sortOption === "price-desc") {
      return list.sort((a, b) => {
        const priceA = getPrice(a);
        const priceB = getPrice(b);
        if (priceA === null && priceB === null) return 0;
        if (priceA === null) return 1;
        if (priceB === null) return -1;
        return priceB - priceA;
      });
    }

    return list.sort((a, b) => {
      const priceA = getPrice(a);
      const priceB = getPrice(b);
      if (priceA === null && priceB === null) return 0;
      if (priceA === null) return 1;
      if (priceB === null) return -1;
      return priceA - priceB;
    });
  }, [items, sortOption, shuffled]);

  const visibleItems = sortedItems.slice(0, visibleCount);
  const hasMore = visibleCount < sortedItems.length;

  return (
    <div className="px-4 pb-12">
      {showOwnSortBar && (
        <div className="mb-6 flex w-full items-center justify-end gap-3">
          <label className="text-xs uppercase tracking-[0.3em] text-slate-500" htmlFor="catalog-sort">
            Sort by
          </label>
          <select
            id="catalog-sort"
            value={sortOption}
            onChange={(event) => setInternalSort(event.target.value as SortOption)}
            className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs uppercase tracking-[0.3em] text-slate-700"
          >
            <option value="popular">Most Popular</option>
            <option value="new-arrivals">New Arrivals</option>
            <option value="price-desc">Price - Highest to Lowest</option>
            <option value="price-asc">Price - Lowest to Highest</option>
          </select>
        </div>
      )}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {visibleItems.map((item) => (
          <Link
            key={item.id}
            href={buildPieceHref(item.id, { backHref, backLabel }) as Parameters<typeof Link>[0]["href"]}
            className="group block"
          >
            <article className="bg-white p-4 text-left transition group-hover:-translate-y-1">
              <div className="relative aspect-[4/3] overflow-hidden bg-white">
                <ItemStatusTag soldOut={item.soldOut} inPossession={item.inPossession} />
                {item.pictureUrl ? (
                  <>
                    {!loadedImages[item.id] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white">
                        <Image src="/tube-spinner-grey.svg" alt="" width={48} height={48} />
                      </div>
                    )}
                    <Image
                      src={item.pictureUrl}
                      alt={item.itemName || "Jewelry item"}
                      fill
                      className="object-contain"
                      sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 320px"
                      onLoadingComplete={() => handleImageLoaded(item.id)}
                    />
                  </>
                ) : (
                  <div className="flex h-full items-center justify-center text-slate-400">Image coming soon</div>
                )}
              </div>
            </article>
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="text-med text-slate-600">{item.itemName || "Untitled"}</h2>
              <span className="text-xs text-slate-900">
                {item.tagPrice ? `$${formatPrice(item.tagPrice)}` : "Request"}
              </span>
            </div>
          </Link>
        ))}
      </div>
      {hasMore && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            className="rounded-sm border border-slate-300 px-8 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-700 transition hover:border-slate-500 hover:text-slate-900"
          >
            Show more ({sortedItems.length - visibleCount} remaining)
          </button>
        </div>
      )}
    </div>
  );
}
